import { useEffect, useState } from 'react';
import { maSignatureApi } from '../../api/resources';
import { SignaturePad } from '../../components/SignaturePad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { C } from '../../theme/palette';

/**
 * Ma signature, dans Réglages → Mon compte : chacun — patron, secrétaire,
 * technicien, comptable — la dessine une fois, puis la pose d'un geste sur
 * ses courriers et ses décharges.
 *
 * @param {object} [props] propriétés du composant
 * @param {(image: string) => void} [props.onEnregistre] reçoit la signature
 *   enregistrée : le courrier ou la décharge en cours la pose aussitôt
 * @returns {JSX.Element} la carte
 */
export function MaSignatureCard({ onEnregistre } = {}) {
  const [signature, setSignature] = useState(null);
  const [message, setMessage] = useState({ etat: '', texte: '' });
  // Le tracé en cours : il ne s'enregistre qu'une fois la signature finie.
  const [brouillon, setBrouillon] = useState('');

  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => maSignatureApi.lit())
      .then((r) => vivant && setSignature(r?.signature || ''))
      .catch(() => vivant && setSignature(''));
    return () => {
      vivant = false;
    };
  }, []);

  const enregistre = async (image) => {
    setMessage({ etat: '', texte: '' });
    try {
      const r = await maSignatureApi.enregistre(image);
      setSignature(r?.signature || '');
      setBrouillon('');
      if (r?.signature) onEnregistre?.(r.signature);
      setMessage({
        etat: 'ok',
        texte: image ? 'Signature enregistrée.' : 'Signature effacée.',
      });
    } catch (e) {
      setMessage({
        etat: 'echec',
        texte: e?.message || 'La signature n’a pas pu être enregistrée.',
      });
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="✒️"
        title="Ma signature"
        color={C.navy}
        sub="Dessinée une fois, posée sur vos courriers et décharges"
      />
      <div style={{ padding: 13 }}>
        {signature === null ? (
          <div role="status" style={{ fontSize: 10, color: C.muted }}>
            Chargement…
          </div>
        ) : signature ? (
          <div>
            <img
              src={signature}
              alt="Ma signature"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: 90,
                background: '#fff',
                border: '1px solid ' + C.border,
                borderRadius: 8,
                marginBottom: 8,
              }}
            />
            <button
              type="button"
              onClick={() => enregistre(null)}
              style={{
                border: '1px solid ' + C.red,
                background: C.white,
                color: C.red,
                borderRadius: 8,
                padding: '7px 10px',
                fontSize: 10,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Effacer et redessiner
            </button>
          </div>
        ) : (
          <div>
            <SignaturePad onSign={setBrouillon} />
            <button
              type="button"
              disabled={!brouillon}
              onClick={() => enregistre(brouillon)}
              style={{
                marginTop: 8,
                width: '100%',
                border: 'none',
                borderRadius: 9,
                padding: '10px',
                fontSize: 11,
                fontWeight: 800,
                cursor: brouillon ? 'pointer' : 'default',
                background: brouillon ? C.navy : C.border,
                color: '#fff',
              }}
            >
              💾 Enregistrer ma signature
            </button>
          </div>
        )}
        {message.texte && (
          <div
            role="status"
            style={{
              fontSize: 10,
              fontWeight: 700,
              marginTop: 6,
              color: message.etat === 'echec' ? C.red : C.green,
            }}
          >
            {message.texte}
          </div>
        )}
      </div>
    </Card>
  );
}
